import { Injectable, OnDestroy } from '@angular/core';
import { BULK_ORDER_NOTIFICATION_EVENT, NOTIFICATION_CONFIG } from '@configs/constants';
import { NotificationService } from '@core/services/notification/notification.service';
import { AppState } from '@core/store/app/app.state';
import { AuthState } from '@core/store/auth/auth.state';
import { environment } from '@env/environment';
import { Store } from '@ngxs/store';
import { SubscriptionChannels } from '@shared/components/notification-drawer/enums/subscription-channels.enum';
import { FirebaseNotification } from '@shared/components/notification-drawer/models/firebase-notification.model';
import { NotificationSnackBarConfigModel } from '@shared/components/notification-drawer/models/snack-bar-config.model';
import { SnackBarNotificationModel } from '@shared/components/notification-drawer/models/snack-bar.model';
import { NotificationType } from '@shared/components/notification-drawer/types/notification.type';
import { OnGoingJobsFilter } from 'app/modules/dashboard/models/on-going-jobs-filter.model';
import { FetchOnGoingJobs } from 'app/modules/dashboard/store/dashboard.actions';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, takeUntil, tap } from 'rxjs/operators';

export enum WorkerStatus {
  INITIALIZING = 'INITIALIZING',
  CONNECTED = 'CONNECTED',
  DISCONNECTED = 'DISCONNECTED',
  ERROR = 'ERROR',
}

export interface WorkerHealthStatus {
  isActive: boolean;
  lastHeartbeat: Date;
  activeChannels: string[];
  connectionCount: number;
}

@Injectable({
  providedIn: 'root',
})
export class FirebaseWorkerService implements OnDestroy {
  private worker: Worker | null = null;
  private readonly destroy$ = new Subject<void>();
  private authSubscription?: Subscription;

  // Public observables
  private readonly notificationsSubject = new Subject<FirebaseNotification>();
  private readonly workerStatusSubject = new BehaviorSubject<WorkerStatus>(WorkerStatus.DISCONNECTED);

  public readonly notifications$ = this.notificationsSubject.asObservable();
  public readonly workerStatus$ = this.workerStatusSubject.asObservable();

  constructor(
    private store: Store,
    private notificationService: NotificationService
  ) {
    this.initializeAuthListener();
  }

  private initializeAuthListener(): void {
    // Listen to auth changes and manage worker lifecycle
    this.authSubscription = this.store
      .select(state => state.auth.token)
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        tap(token => {
          if (!token) {
            this.terminateWorker();
          } else {
            // Small delay to ensure auth state is fully settled
            setTimeout(() => this.initializeWorker(), 100);
          }
        })
      )
      .subscribe();
  }

  public initializeWorker(): void {
    if (this.worker) {
      console.warn('Worker already exists, terminating before reinitializing');
      this.terminateWorker();
    }

    if (typeof Worker === 'undefined') {
      console.error('Web Workers not supported');
      this.workerStatusSubject.next(WorkerStatus.ERROR);
      return;
    }

    const authSnapshot = this.store.selectSnapshot(s => s.auth);
    if (!authSnapshot.user?.orgId || !authSnapshot.user?.orgUserId) {
      console.warn('User not properly authenticated, skipping worker initialization');
      this.workerStatusSubject.next(WorkerStatus.ERROR);
      return;
    }

    try {
      this.workerStatusSubject.next(WorkerStatus.INITIALIZING);

      this.worker = new Worker(
        new URL('../../../shared/components/notification-drawer/firebase-notifications.worker.ts', import.meta.url),
        { type: 'module' }
      );

      // Send initialization data
      this.worker.postMessage({
        environment,
        orgId: authSnapshot.user.orgId,
        userId: authSnapshot.user.orgUserId,
      });

      // Set up message listener
      this.worker.onmessage = ({ data }) => {
        this.handleWorkerMessage(data);
      };

      // Set up error listener
      this.worker.onerror = error => {
        console.error('Worker error:', error);
        this.workerStatusSubject.next(WorkerStatus.ERROR);
        // Attempt to reconnect after delay
        setTimeout(() => this.initializeWorker(), 5000);
      };

      this.workerStatusSubject.next(WorkerStatus.CONNECTED);
    } catch (error) {
      console.error('Failed to initialize worker:', error);
      this.workerStatusSubject.next(WorkerStatus.ERROR);
    }
  }

  private handleWorkerMessage(notification: FirebaseNotification): void {
    try {
      // Broadcast to all subscribers
      this.notificationsSubject.next(notification);

      // Show toast notification (this was previously in the component)
      this.showToastNotification(notification);

      // Trigger any side effects (like refreshing ongoing jobs)
      this.handleNotificationSideEffects(notification);
    } catch (error) {
      console.error('Error handling worker message:', error);
    }
  }

  private showToastNotification(notification: FirebaseNotification): void {
    try {
      const snackBarData = this.prepareSnackBarData(notification);
      this.notificationService.showSnackBarAlert(
        snackBarData.title,
        snackBarData.body,
        this.getNotificationType(notification.receivedChannel),
        snackBarData.url
      );
    } catch (error) {
      console.error('Error showing toast notification:', error);
    }
  }

  private handleNotificationSideEffects(notification: FirebaseNotification): void {
    // Handle bulk order notification event (previously in component)
    if (notification.event === BULK_ORDER_NOTIFICATION_EVENT) {
      const filter = {
        orgId: this.store.selectSnapshot(AuthState.getOrgId),
        from: null,
        to: null,
        searchKey: null,
        operationId: this.store.selectSnapshot(AppState.getCurrentOperationId),
      } as OnGoingJobsFilter;
      this.store.dispatch(new FetchOnGoingJobs(filter));
    }
  }

  public terminateWorker(): void {

    const worker = this.worker;
    if (!worker) return;

    this.worker = null;
    this.workerStatusSubject.next(WorkerStatus.DISCONNECTED);

    try {
      // Let the worker unsubscribe its Firestore listeners before the thread dies
      worker.postMessage({ command: 'terminate' });
      setTimeout(() => worker.terminate(), 100);
    } catch (error) {
      console.error('Error terminating worker:', error);
      worker.terminate();
    }
  }

  public forceReconnect(): void {
    console.log('Force reconnecting worker...');
    this.terminateWorker();
    setTimeout(() => this.initializeWorker(), 500);
  }

  public getWorkerHealth(): WorkerHealthStatus {
    return {
      isActive: this.worker !== null,
      lastHeartbeat: new Date(), // Could be enhanced with actual heartbeat
      activeChannels: ['JOB_RELATED', 'OTHER', 'HIGH_PRIORITY', 'WAR_ROOM', 'YOU_ARE_HERE_RELATED'],
      connectionCount: this.worker ? 1 : 0,
    };
  }

  // Helper methods (extracted from component)
  private prepareSnackBarData(notification: FirebaseNotification): SnackBarNotificationModel {
    return {
      title: this.formatTemplate(notification.titleTemplate, notification.titleTemplateMetaData),
      body: this.formatTemplate(notification.bodyTemplate, notification.bodyTemplateMetaData),
      type: this.getNotificationConfig(notification.receivedChannel),
      url: notification.url,
    };
  }

  private formatTemplate(template: string, metadata: Record<string, string>): string {
    if (!metadata) {
      return template;
    }

    return template.replace(/\{([^}]+)\}([.,;:!?])?/g, (match, key, punctuation) => {
      const value = metadata[key];
      if (value === undefined) return match;

      // TODO: change after release
      if (key === 'extendedDateTime') {
        return this.formatDate(value);
      }

      const formattedValue = value;
      return formattedValue + (punctuation || '');
    });
  }

  private formatDate(dateString: string): string {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }

  private getNotificationConfig(channel: string): NotificationSnackBarConfigModel {
    if (channel === SubscriptionChannels.WAR_ROOM) {
      return NOTIFICATION_CONFIG.textMessage;
    } else if (channel === SubscriptionChannels.HIGH_PRIORITY) {
      return NOTIFICATION_CONFIG.warning;
    } else if (channel === SubscriptionChannels.JOB_RELATED) {
      return NOTIFICATION_CONFIG.info;
    } else if (channel === SubscriptionChannels.OTHER) {
      return NOTIFICATION_CONFIG.success;
    } else {
      return NOTIFICATION_CONFIG.info;
    }
  }

  private getNotificationType(channel: string): NotificationType {
    if (channel === SubscriptionChannels.WAR_ROOM) {
      return 'textMessage';
    } else if (channel === SubscriptionChannels.HIGH_PRIORITY) {
      return 'warning';
    } else if (channel === SubscriptionChannels.JOB_RELATED) {
      return 'info';
    } else if (channel === SubscriptionChannels.OTHER) {
      return 'success';
    } else {
      return 'info';
    }
  }

  ngOnDestroy(): void {
    console.log('FirebaseWorkerService destroying...');
    this.terminateWorker();
    this.authSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
